import dayjs from 'dayjs';
import i18n from '../../i18n';

/**
 * Ce que la colonne « Ancienneté » d'un bordereau doit dire, selon son statut.
 *
 * LE DEFAUT QU'ELLE CORRIGE ETAIT UNE CONDITION INVERSEE :
 *
 *     if (r.statut !== 'REMIS_TRANSPORTEUR') return 'Remis au transporteur';
 *
 * La condition dit « si ce bordereau n'est PAS remis » et la cellule affichait
 * « Remis au transporteur ». Quatre statuts sur cinq tombaient dans cette branche : un
 * brouillon jamais sorti du bureau s'annoncait comme remis, un bordereau deja
 * decharge aussi - alors qu'il a depasse cette etape. La colonne « Statut », juste a
 * cote, disait l'inverse, et c'est elle qu'on finissait par soupconner.
 *
 * POURQUOI L'INSTANT EST UN PARAMETRE. « Sans decharge depuis N jours » se compte
 * contre aujourd'hui : fige dans la fonction, le calcul ne serait pas verifiable
 * autrement qu'en attendant le lendemain.
 *
 * CE QU'ELLE REND : `{ texte, enRetard }`, et non du balisage. Le composant decide de
 * l'habillage ; cette fonction decide du sens, et c'est le sens qui etait faux.
 */

/** Au-dela, un bordereau remis sans decharge est relancable. */
export const SEUIL_RELANCE_JOURS = 15;

const jours = (de, a) => {
  if (!de || !a) return null;
  const n = dayjs(a).diff(dayjs(de), 'day');
  return Number.isFinite(n) ? n : null;
};

const RIEN = { texte: '-', enRetard: false };

export function ancienneteBordereau(bordereau, maintenant = new Date()) {
  if (!bordereau || !bordereau.statut) return RIEN;

  switch (bordereau.statut) {
    // Pas encore sorti du bureau. C'est le cas qui s'annoncait « remis ».
    case 'BROUILLON':
    case 'IMPRIME':
      return { texte: i18n.t('courriers:bordereaux.anciennete.pasEncoreRemis'), enRetard: false };

    case 'REMIS_TRANSPORTEUR': {
      const attente = jours(bordereau.dateRemiseTransporteur, maintenant);
      /* DATE ABSENTE : on le dit, plutot que d'afficher « null j sans décharge ».
         L'application pose le statut et la date ensemble, mais une donnee reprise hors
         de ce chemin ne le garantit pas. */
      if (attente === null) return { texte: i18n.t('courriers:bordereaux.anciennete.dateInconnue'), enRetard: false };
      return {
        texte: i18n.t('courriers:bordereaux.joursSansDecharge', { n: attente }),
        enRetard: attente >= SEUIL_RELANCE_JOURS,
      };
    }

    case 'DECHARGE_RECUE': {
      /* LE CHIFFRE QUI MESURE UN TRANSPORTEUR : l'ecart entre la remise et la
         decharge. Sans l'une des deux dates, on ne l'invente pas. */
      const delai = jours(bordereau.dateRemiseTransporteur, bordereau.dateDechargeRecue);
      return {
        texte: delai === null
          ? i18n.t('courriers:bordereaux.anciennete.decharge')
          : i18n.t('courriers:bordereaux.anciennete.dechargeEn', { n: delai }),
        enRetard: false,
      };
    }

    case 'RETOURNE':
      return { texte: i18n.t('courriers:bordereaux.anciennete.retourne'), enRetard: false };

    /* Un statut ajoute cote serveur reste lisible plutot que de laisser un blanc,
       qu'on lirait comme une donnee manquante. */
    default:
      return RIEN;
  }
}
